/* ============================================================
   SAM-608 — GET /business_access/combined: query builder and response types.

   The contract lives in sam-lambda-functions, functions/businessAccess/combined_access.py
   (handle()). What matters on this side:
   - Named params: page, limit, entity, q, status, business_id (uuid), date_from, date_to
     (YYYY-MM-DD = whole days, date_to inclusive; or an ISO date-time).
   - Any other key is an equality filter on a column and must match ^[a-z_][a-z0-9_]*$,
     otherwise the whole request answers 400 "Unknown filter(s)". A key that matches but is
     not a column makes that side's query fail and come back empty, so only real columns
     are sent (see COLUMN_FILTERS).
   - date window: a row matches when its visit [start, end] overlaps the window.
   - Pagination is SQL-side: total_elements / total_pages are the real totals.
   - business_access rows carry booking_id and a `booking` summary (null when the pass was
     not granted by a booking); invitation rows always carry booking: null.
   ============================================================ */
import { businessAccessApi } from './businessAccessClient';

/** The `booking` object on a business_access row (combined_access.get_business_access_fast). */
export interface CombinedBooking {
  id: string;
  confirmation_code?: string | null;
  service_name?: { en?: string | null; es?: string | null } | null;
  reservation_start?: string | null;
  reservation_end?: string | null;
  /** booking_status.name, e.g. "confirmed". */
  status?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  payment_status?: string | null;
}

/** Guest fields the endpoint returns (main_guest and each business_guest). */
export interface CombinedGuestFields {
  photo_url?: string | null;
  idtype?: string | null;
  /** From the pass's guest snapshot, e.g. "verified". Absent when not set. */
  status?: string | null;
}

export type CombinedEntity = 'business_access' | 'invitation';

export interface CombinedQuery {
  page?: number;
  limit?: number;
  q?: string;
  /**
   * SAM-575: status by name ("Checked Out"), matched case-insensitively on both sides.
   * Its own parameter on purpose — it used to ride in `q`, which is free text, so a tab
   * pinning a status and the user's search overwrote each other. By name and not by id
   * because invitation_status has several rows sharing a name.
   */
  status?: string;
  entity?: CombinedEntity;
  business_id?: string;
  date_from?: string;
  date_to?: string;
  /** Residential scope for property managers (invitation.estate_id). */
  estate_id?: string | number;
  host_user_id?: string;
  business_access_status_id?: number;
  booking_id?: string;
  /** One pass by id (business_access.id); pair with entity: 'business_access'. */
  id?: string;
  /**
   * SAM-307: door PIN generation status ('pending' | 'ok' | 'failed' | 'skipped'), an
   * equality filter like the others. Needs the hik_pin_status column the backend adds; until
   * it exists the handler answers that side empty, which is why only the opt-in
   * "door PIN not OK" view sends it.
   */
  hik_pin_status?: 'pending' | 'ok' | 'failed' | 'skipped';
}

/** Equality filters we send; each is a real column of business_access or invitation. */
const COLUMN_FILTERS = ['estate_id', 'host_user_id', 'business_access_status_id', 'booking_id', 'id', 'hik_pin_status'] as const;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
// SAM-849: the Lambda answers 500 over 6 MB; 10000 rows did not fit. Client-side cap: the backend
// (_COMBINED_MAX_LIMIT) still accepts 10000 until SAM-849b lowers it and answers 413.
export const COMBINED_MAX_LIMIT = 1000;
/** Rows per request when a whole result set is pulled (exports). */
export const COMBINED_EXPORT_PAGE_SIZE = 500;

const isDateParam = (v: string) => DAY_RE.test(v) || (/^\d{4}-\d{2}-\d{2}[T ]/.test(v) && !Number.isNaN(Date.parse(v)));

/**
 * Query string for /combined. Drops anything the backend would answer 400 for (a bad
 * business_id, a malformed date, a window with from after to) instead of sending it, and
 * clamps page/limit to what the handler accepts.
 */
export function buildCombinedParams(query: CombinedQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: Math.max(1, Math.floor(Number(query.page) || 1)),
    limit: Math.min(COMBINED_MAX_LIMIT, Math.max(1, Math.floor(Number(query.limit) || 10))),
  };
  const q = query.q?.trim();
  if (q) out.q = q;
  const status = query.status?.trim();
  if (status) out.status = status;
  if (query.entity === 'business_access' || query.entity === 'invitation') out.entity = query.entity;
  if (query.business_id && UUID_RE.test(String(query.business_id).trim())) {
    out.business_id = String(query.business_id).trim();
  }
  const from = query.date_from && isDateParam(query.date_from) ? query.date_from : undefined;
  const to = query.date_to && isDateParam(query.date_to) ? query.date_to : undefined;
  if (from && to && from.slice(0, 10) > to.slice(0, 10)) {
    // The handler answers 400 for a reversed window; a reversed range picker means the same window.
    out.date_from = to;
    out.date_to = from;
  } else {
    if (from) out.date_from = from;
    if (to) out.date_to = to;
  }
  COLUMN_FILTERS.forEach((key) => {
    const value = query[key];
    if (value === undefined || value === null || value === '') return;
    out[key] = typeof value === 'number' ? value : String(value);
  });
  return out;
}

export interface CombinedResponse<Row> {
  data: Row[];
  total_pages: number;
  page: number;
  limit?: number;
  total_elements: number;
}

export function fetchCombined<Row>(query: CombinedQuery): Promise<CombinedResponse<Row>> {
  return businessAccessApi<CombinedResponse<Row>>({
    url: 'combined',
    method: 'GET',
    params: buildCombinedParams(query),
  });
}

/** A page of a whole-set fetch failed: the set is incomplete, so nothing is returned. */
export class CombinedPageError extends Error {
  constructor(readonly page: number, readonly totalPages: number, readonly loaded: number, readonly original?: unknown) {
    super(`Page ${page} of ${totalPages} failed after ${loaded} rows`);
    this.name = 'CombinedPageError';
  }
}

/**
 * Every row of a /combined query, one page at a time (SAM-849). Pages are sequential so the
 * Lambda never builds a response near 6 MB. If any page fails it throws CombinedPageError
 * instead of returning a partial set. `onProgress(loaded, total)` fires after each page.
 */
export async function fetchCombinedAll<Row extends { id?: unknown; entity?: unknown }>(
  query: CombinedQuery,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Row[]> {
  const rows: Row[] = [];
  const seen = new Set<string>();
  let page = 1;
  let totalPages = 1;
  let total = 0;
  while (page <= totalPages) {
    let res: CombinedResponse<Row>;
    try {
      res = await fetchCombined<Row>({ ...query, page, limit: COMBINED_EXPORT_PAGE_SIZE });
    } catch (e) {
      throw new CombinedPageError(page, totalPages, rows.length, e);
    }
    totalPages = Math.max(1, Number(res.total_pages) || 1);
    total = Number(res.total_elements) || total;
    const data = res.data || [];
    for (const row of data) {
      // A pass created mid-export shifts later pages: skip a row already collected.
      const key = row.id == null ? null : `${String(row.entity ?? '')}:${String(row.id)}`;
      if (key) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      rows.push(row);
    }
    onProgress?.(rows.length, Math.max(total, rows.length));
    if (data.length === 0) break;
    page += 1;
  }
  return rows;
}

/** Local calendar day as YYYY-MM-DD (the backend treats a plain date as a whole day). */
export function toDayParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Display name of a combined booking's service (English first, like the rest of the admin). */
export function combinedBookingService(b: CombinedBooking | null | undefined): string {
  return b?.service_name?.en || b?.service_name?.es || 'Booking';
}

/** "confirmed" -> "Confirmed", "no_show" -> "No show". */
export function combinedBookingStatus(b: CombinedBooking | null | undefined): string {
  const s = String(b?.status || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Deep link to a booking in the Bookings console (Bookings.tsx reads ?booking= and ?business=). */
export function bookingConsoleLink(bookingId: string, businessId?: string | null): string {
  const p = new URLSearchParams({ booking: bookingId });
  if (businessId) p.set('business', businessId);
  return `/bookings?${p.toString()}`;
}

/** Deep link to a pass in the business page's Activity (BusinessActivity reads ?pass=). */
export function passActivityLink(businessId: string, passId: string): string {
  const p = new URLSearchParams({ tab: 'activity', pass: passId });
  return `/business/${encodeURIComponent(businessId)}?${p.toString()}`;
}

/** Deep link to a pass's detail in the Passes page (Invitations.tsx reads ?pass=). */
export function passDetailLink(passId: string): string {
  return `/invitations?${new URLSearchParams({ pass: passId }).toString()}`;
}
